import axios, { AxiosRequestConfig, AxiosResponse, Method, RawAxiosRequestHeaders } from 'axios';
import { ServerError } from './utils/errors';
import { InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

// @TODO Refactor: Make all get, post, patch, put, delete methods lightweight, by moving also the error handling into the request() method

export const msalConfig = {
    auth: {
        clientId: '74e6ee87-2414-41ad-8a06-96c4000ed124', // This is the ONLY mandatory field that you need to supply.
        authority: 'https://login.microsoftonline.com/5cfb90b0-47df-4f67-8a71-77c66a05ff83/', // Replace the placeholder with your tenant subdomain
        redirectUri: import.meta.env.MICROSOFT_REDIRECT_URL, // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        iframeHashTimeout: 100000,
        navigateFrameWait: 500,
        loggerOptions: {
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ['api://74e6ee87-2414-41ad-8a06-96c4000ed124/User.Read'],
    redirectUri: msalConfig.auth.redirectUri,
};

export class BaseSdk {
    readonly outreachSettingsPath = `outreach-settings`;
    readonly usersPath = `users`;
    readonly userAdminPath = `admin/users`;
    readonly userOrganizationsPath = `user-organizations`;
    readonly prospectsPath = `prospects`;
    readonly targetSearchesPath = `target-searches`;
    readonly authenticationPath = `authentication`;
    readonly sourcesPath = `sources`;
    readonly oauthPath = `oauth`;
    readonly search = `search`;
    readonly rootPath = ``;
    readonly trackersPath = 'trackers';
    readonly tasksPath = 'tasks';
    readonly discountsPath = 'discounts';
    readonly assistantPath = 'assistant';
    readonly groupsPath = 'groups';
    readonly userContactsPath = 'user-contacts';
    readonly prospectCollectionsPath = 'prospect-collections';

    apiEndpoint: string;
    authProvider?: IPublicClientApplication;

    constructor(apiEndpoint: string, authProvider?: IPublicClientApplication) {
        this.apiEndpoint = apiEndpoint;
        this.authProvider = authProvider;
    }

    protected async request<T>(
        path: string,
        method: Method,
        _config?: AxiosRequestConfig,
        retriesLeft = 1,
    ): Promise<AxiosResponse<T>> {
        let config: AxiosRequestConfig | undefined;
        try {
            config = await this.getRequestConfig(_config);
            const result = await axios<T>(`${this.apiEndpoint}/${path}`, {
                method: method,
                ...config,
            });
            return result;
        } catch (error) {
            const errorStatus = (error as any)?.response?.status;
            if (errorStatus === 401 && retriesLeft > 0) {
                console.log('before token');
                const token = await this.getAccessToken();
                console.log('after token', token);
                // await sleep(3000);
                return await this.request(path, method, config || _config, retriesLeft - 1);
            }
            throw error;
        }
    }

    private async getAccessToken(): Promise<string | null> {
        if (!this.authProvider || !this.authProvider.getActiveAccount() || !msalConfig) {
            return null;
        }

        const account = this.authProvider.getActiveAccount();
        const accessTokenRequest = {
            scopes: [import.meta.env.VITE_MICROSOFT_SCOPE],
            account: account || undefined,
            redirectUri: msalConfig.auth.redirectUri,
        };
        console.log('accessTokenRequest', accessTokenRequest);
        try {
            const response = this.authProvider
                .acquireTokenSilent(accessTokenRequest)
                .then(() => console.log('badum tss'));

            return '';
            // return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                if (!this.authProvider || !this.authProvider.getActiveAccount()) {
                    return null;
                }

                try {
                    await this.authProvider.acquireTokenPopup(accessTokenRequest);
                } catch (error) {
                    console.log('[acquire] token interactive failure', error);
                    return null;
                }
            }

            console.log('[acquire] Unknown auth error', error, error?.constructor?.name);
            return null;
        }
    }

    protected async get<T>(path: string): Promise<T | null> {
        try {
            const result = await this.request<T>(path, 'GET');
            return result?.data || null;
        } catch (error) {
            const errorStatus = (error as any)?.response?.status;
            if (errorStatus === 404) {
                return null;
            }
            throw new ServerError(`HTTP GET failed, error: ${(error as any).message}`);
        }
    }

    protected async post<T>({
        path,
        payload,
        callback,
        headers,
    }: {
        path: string;
        payload?: {};
        callback?: (...args: any) => any;
        headers?: { [key: string]: string };
    }): Promise<T> {
        try {
            const result = await this.request<T>(path, 'POST', {
                headers,
                data: payload,
            });
            return result.data;
        } catch (error) {
            // Error handling is strange over here.
            // If we combine what callback does with the exception style it would be great.
            // maybe we should introduce something like v2 version of this one?
            if (callback) {
                return callback(JSON.stringify((error as any).response));
            } else if ((error as any)?.response?.data?.type === 'ClientPresentableException') {
                throw (error as any).response?.data;
            }

            throw new ServerError(`HTTP POST failed, error: ${(error as any).message}`);
        }
    }

    protected async put<T>(path: string, payload?: object, callback?: (...args: any) => any): Promise<T> {
        try {
            const result = await this.request<T>(path, 'PUT', {
                data: payload,
            });
            return result.data;
        } catch (error) {
            if (callback) {
                return callback(JSON.stringify((error as any).response));
            }

            if ((error as any).response?.data?.payload) {
                throw new Error(`${JSON.stringify((error as any).response?.data)}`);
            }
            throw new ServerError(`HTTP PUT failed, error: ${(error as any).response?.data || (error as any).message}`);
        }
    }

    protected async patch<T>(path: string, payload?: object): Promise<T> {
        try {
            const result = await this.request<T>(path, 'PATCH', {
                data: payload,
            });
            return result.data;
        } catch (error) {
            throw new ServerError(`HTTP PATCH failed, error: ${(error as any).message}`);
        }
    }

    protected async delete<T>(path: string, payload?: object): Promise<T> {
        try {
            const result = await this.request<T>(path, 'DELETE', {
                data: payload,
            });
            return result.data;
        } catch (error) {
            throw new ServerError(`HTTP DELETE failed, error: ${(error as any).message}`);
        }
    }

    private async getRequestConfig(options: AxiosRequestConfig = {}): Promise<AxiosRequestConfig> {
        console.log('curr account', this.authProvider?.getActiveAccount());
        const token = this.authProvider?.getActiveAccount()?.idToken as string;
        const config: AxiosRequestConfig = {
            headers: this.getRequestConfigHeaders(token, options.headers),
        };
        if (options.params) {
            config.params = options.params;
        }
        if (options.data) {
            config.data = options.data;
        }
        return config;
    }

    //Not using this one anymore, header is created inside get and post functions below
    // an 'any' is used as it will require deeper understanding of types in AxiosRequestConfig
    private getRequestConfigHeaders(
        authenticationToken: string,
        headers?: RawAxiosRequestHeaders,
    ): RawAxiosRequestHeaders {
        const authenticateAsUserId = localStorage.getItem('x-authenticate-as-user');

        const requestHeaders: RawAxiosRequestHeaders = !authenticationToken
            ? {}
            : {
                  AcceptEncoding: 'gzip',
                  ...(authenticateAsUserId && {
                      'x-authenticate-as-user': authenticateAsUserId,
                  }),
                  ...(!!headers && headers),
                  Authorization: `Bearer ${authenticationToken}`,
              };

        return requestHeaders;
    }
}
